import { parseComposition, type CompositionDocument } from './document';
import { openCompositionDatabase } from './storage';

export type SyncRequest = {
  ownerId: string;
  id: string;
  localRevision: number;
  operationId: string;
  expectedRevision: number;
  document: CompositionDocument;
};

// All outbox and document changes use the same transaction as their revision checks.
async function transaction<T>(
  run: (tx: IDBTransaction, result: (value: T) => void) => void,
): Promise<T> {
  const db = await openCompositionDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(['documents', 'outbox', 'history'], 'readwrite');
      let result: T;
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Composition sync storage was interrupted.'));
      run(tx, (value) => {
        result = value;
      });
    });
  } finally {
    db.close();
  }
}

function putDocument(tx: IDBTransaction, document: CompositionDocument) {
  tx.objectStore('documents').put(document);
  const history = tx.objectStore('history');
  history.put(document);
  const keys = history.index('document').getAllKeys([document.ownerId, document.id]);
  keys.onsuccess = () => keys.result.slice(0, -12).forEach((key) => history.delete(key));
}

/** Freeze a durable request before sending. Retries after a crash reuse exactly this payload. */
export function nextSyncRequest(ownerId: string, id: string): Promise<SyncRequest | undefined> {
  return transaction((tx, done) => {
    const pending = tx.objectStore('outbox').get([ownerId, id]);
    pending.onsuccess = () => {
      if (pending.result) {
        done(pending.result);
        return;
      }
      const read = tx.objectStore('documents').get([ownerId, id]);
      read.onsuccess = () => {
        const document: CompositionDocument | undefined = read.result;
        if (!document || document.cloud?.syncedLocalRevision === document.revision)
          return done(undefined);
        const request = {
          ownerId,
          id,
          localRevision: document.revision,
          operationId: crypto.randomUUID(),
          expectedRevision: document.cloud?.revision ?? 0,
          document,
        };
        tx.objectStore('outbox').put(request);
        done(request);
      };
    };
  });
}

export function acknowledgeSync(request: SyncRequest, remote: CompositionDocument): Promise<void> {
  return transaction((tx, done) => {
    const outbox = tx.objectStore('outbox');
    const pending = outbox.get([request.ownerId, request.id]);
    pending.onsuccess = () => {
      if (pending.result?.operationId !== request.operationId) return done();
      const read = tx.objectStore('documents').get([request.ownerId, request.id]);
      read.onsuccess = () => {
        const current: CompositionDocument = read.result;
        if (current) {
          // A newer local edit remains dirty even when this older upload succeeds.
          tx.objectStore('documents').put({
            ...current,
            cloud: { revision: remote.revision, syncedLocalRevision: request.localRevision },
          });
        }
        outbox.delete([request.ownerId, request.id]);
        done();
      };
    };
  });
}

function localRemote(remote: CompositionDocument, revision: number): CompositionDocument {
  return {
    ...remote,
    revision,
    cloud: { revision: remote.revision, syncedLocalRevision: revision },
  };
}

export function receiveComposition(ownerId: string, value: CompositionDocument): Promise<void> {
  const remote = parseComposition(value);
  if (remote.ownerId !== ownerId)
    throw new Error('Composition account does not match the signed-in account.');
  return transaction((tx, done) => {
    const read = tx.objectStore('documents').get([ownerId, remote.id]);
    read.onsuccess = () => {
      const current: CompositionDocument | undefined = read.result;
      if (
        !current ||
        (current.cloud?.syncedLocalRevision === current.revision &&
          current.cloud.revision < remote.revision)
      ) {
        putDocument(tx, localRemote(remote, (current?.revision ?? 0) + 1));
      }
      done();
    };
  });
}

/** Keep the latest local edit as a separate sketch before installing the cloud version. */
export function preserveSyncConflict(
  request: SyncRequest,
  remote: CompositionDocument,
): Promise<string | undefined> {
  parseComposition(remote);
  if (remote.ownerId !== request.ownerId || remote.id !== request.id)
    throw new Error('Unexpected composition conflict response.');
  return transaction((tx, done) => {
    const outbox = tx.objectStore('outbox');
    const pending = outbox.get([request.ownerId, request.id]);
    pending.onsuccess = () => {
      if (pending.result?.operationId !== request.operationId) return done(undefined);
      const read = tx.objectStore('documents').get([request.ownerId, request.id]);
      read.onsuccess = () => {
        const current: CompositionDocument = read.result;
        const copy: CompositionDocument = {
          ...current,
          id: crypto.randomUUID(),
          title: `${current.title.slice(0, 140)} (conflict copy)`,
          conflictOf: current.id,
          revision: 1,
          cloud: undefined,
          updatedAt: new Date().toISOString(),
        };
        putDocument(tx, copy);
        putDocument(tx, localRemote(remote, current.revision + 1));
        outbox.delete([request.ownerId, request.id]);
        done(copy.id);
      };
    };
  });
}
